/**
 * list_tester_notes Tool — list tester knowledge-base notes (Issue #2661)
 *
 * Mirrors the `runhuman notes list` CLI surface. Use this to discover note IDs
 * before update_tester_note / delete_tester_note.
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { JobClient, ListTesterNotesParams } from '../clients/job-client.js';
import type { TesterNoteTag } from '@runhuman/shared';

const NOTE_TAGS: readonly TesterNoteTag[] = [
  'credentials',
  'environment',
  'setup',
  'procedures',
  'known-issues',
  'contacts',
  'general',
];

export type ListTesterNotesArgs = ListTesterNotesParams;

export const listTesterNotesToolDefinition: Tool = {
  name: 'list_tester_notes',
  description: `List tester knowledge-base notes visible to the caller.

Tester notes are persistent knowledge entries (credentials, environment details, procedures) scoped to an organization and optionally a project. Use this to find a note ID before calling update_tester_note or delete_tester_note.

Filter by organization or project, search title and body, or filter by tag.`,
  inputSchema: {
    type: 'object',
    properties: {
      organizationId: {
        type: 'string',
        description: 'Restrict to one organization. Returns 403 if the caller lacks access.',
      },
      projectId: {
        type: 'string',
        description: 'Restrict to one project. Org-wide notes are also returned when set.',
      },
      search: {
        type: 'string',
        description: 'Full-text search across title and body.',
      },
      tags: {
        type: 'array',
        items: { type: 'string', enum: NOTE_TAGS as unknown as string[] },
        description: `Filter by tag. Allowed: ${NOTE_TAGS.join(', ')}.`,
      },
      limit: {
        type: 'number',
        description: 'Maximum number of notes to return.',
        minimum: 1,
      },
      offset: {
        type: 'number',
        description: 'Skip this many notes (for pagination).',
        minimum: 0,
      },
    },
  },
};

export async function handleListTesterNotes(
  args: ListTesterNotesArgs,
  jobClient: JobClient,
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  try {
    const data = await jobClient.listTesterNotes(args);

    if (data.items.length === 0) {
      return {
        content: [{
          type: 'text',
          text: `No tester notes matched your filters.

Tip: omit filters to see every note you can access, or pass a different organizationId/projectId.`,
        }],
      };
    }

    const noteList = data.items.map((note) => {
      const lines = [`- ${note.title} (id: ${note.id})`];
      lines.push(`  Org: ${note.organizationName} (${note.organizationId})`);
      lines.push(
        note.projectId
          ? `  Project: ${note.projectName ?? note.projectId} (${note.projectId})`
          : `  Project: org-wide (no project)`,
      );
      if (note.tags.length > 0) lines.push(`  Tags: ${note.tags.join(', ')}`);
      lines.push(`  Visibility: ${note.isInternal ? 'internal' : 'customer-visible'}`);
      lines.push(`  Edited: ${note.editedAt} by ${note.editedByEmail}`);
      return lines.join('\n');
    }).join('\n\n');

    return {
      content: [{
        type: 'text',
        text: `Found ${data.items.length} of ${data.total} tester note(s):

${noteList}

To edit one: update_tester_note(noteId: "<id>", ...)
To archive: delete_tester_note(noteId: "<id>")`,
      }],
    };
  } catch (error) {
    const err = error as Error & { status?: number };
    return {
      content: [{
        type: 'text',
        text: `Failed to list tester notes\n\nError: ${err.message}${err.status ? `\nStatus: ${err.status}` : ''}`,
      }],
      isError: true,
    };
  }
}
